import React from "react";
import { buildWorker360Metrics } from "../utils/worker360Metrics";

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}


function displayValue(value, fallback = "—") {
  return value !== undefined && value !== null && value !== ""
    ? value
    : fallback;
}

function MetricCard({ label, value }) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color: "#0f172a",
          fontSize: 22,
          fontWeight: 950,
        }}
      >
        {displayValue(value)}
      </div>
    </div>
  );
}

export default function Worker360Overview({
  worker,
  shifts = [],
}) {
  if (!worker) return null;

  const shiftMetrics = buildWorker360Metrics(
    worker,
    shifts
  );

  const name =
    worker.displayName ||
    worker.fullName ||
    `${worker.firstName || ""} ${worker.lastName || ""}`.trim() ||
    "Worker";

  const role =
    worker.primaryRole ||
    worker.position ||
    worker.workerType ||
    worker.role ||
    "Worker";

  const facility =
    worker.facilityName ||
    worker.primaryFacilityName ||
    worker.facilityId ||
    "Not assigned";

  const cityState = [worker.city, worker.state].filter(Boolean).join(", ");

  const workerStatus = normalize(
    worker.workerOperationalState ||
      worker.workerStatus ||
      worker.status
  );

  const isReady =
    workerStatus === "ready" ||
    workerStatus === "active" ||
    workerStatus === "ready_for_shifts";

  const complianceStatus = normalize(worker.complianceStatus);

  const complianceScore =
    worker.complianceScore ??
    (complianceStatus === "approved" ? 100 : "Pending");

 const reliabilityScore =
  shiftMetrics.reliability ??
  worker.reliabilityScore ??
  worker.reliability ??
  worker.reliabilityPercent ??
  "—";

const attendance =
  shiftMetrics.attendance ??
  worker.attendancePercent ??
  worker.attendanceRate ??
  worker.attendanceScore ??
  "—";

 const completedShifts =
  shiftMetrics.completedCount ||
  worker.completedShiftCount ||
  worker.completedShifts ||
  0;

const hoursWorked =
  shiftMetrics.totalHours
  worker.totalHoursWorked ||
  worker.hoursWorked ||
  worker.totalHours ||
  0;

const facilitiesWorked =
  shiftMetrics.facilitiesWorked ||
  worker.facilitiesWorkedCount ||
  worker.facilityCount ||
  (Array.isArray(worker.facilityIds)
    ? worker.facilityIds.length
    : worker.facilityId
    ? 1
    : 0);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          padding: 18,
          borderRadius: 18,
          color: "#ffffff",
          background:
            "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 950,
              }}
            >
              {name}
            </div>

            <div
              style={{
                marginTop: 5,
                color: "#dbeafe",
                fontWeight: 800,
              }}
            >
              {role} · {facility}
            </div>

            {cityState ? (
              <div
                style={{
                  marginTop: 4,
                  color: "#bfdbfe",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {cityState}
              </div>
            ) : null}
          </div>

          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              color: isReady ? "#166534" : "#92400e",
              background: isReady ? "#dcfce7" : "#fef3c7",
              fontSize: 11,
              fontWeight: 950,
              textTransform: "uppercase",
            }}
          >
            {isReady ? "Ready for Shifts" : displayValue(workerStatus, "Pending")}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
          gap: 10,
        }}
      >
        <MetricCard label="Reliability" value={reliabilityScore} />
              <MetricCard
                  label="Attendance"
                  value={
                      typeof attendance === "number"
                          ? `${attendance}%`
                          : attendance
                  }
              />
        <MetricCard label="Compliance" value={complianceScore} />
        <MetricCard label="Completed Shifts" value={completedShifts} />
              <MetricCard
                  label="Hours Worked"
                  value={Number(hoursWorked).toFixed(1)}
              />
        <MetricCard label="Facilities Worked" value={facilitiesWorked} />
      </div>

      <div
        style={{
          padding: 16,
          borderRadius: 16,
          border: "1px solid #ddd6fe",
          background: "#faf5ff",
        }}
      >
        <div
          style={{
            color: "#6d28d9",
            fontSize: 16,
            fontWeight: 950,
          }}
        >
          AI Workforce Summary
        </div>

        <div
          style={{
            marginTop: 10,
            display: "grid",
            gap: 7,
            color: "#334155",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          <div>• Worker profile is active and available for review.</div>
          <div>
            • Compliance status:{" "}
            {displayValue(worker.complianceStatus, "Not available")}.
          </div>
          <div>• Reliability information is ready for planner use.</div>
          <div>• Shift and performance insights will update from live data.</div>
        </div>
      </div>
    </div>
  );
}